import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import imageCompression from 'browser-image-compression';
import { MediaService } from './media.service';

export interface ImageVariants {
  original: string;
  webp1400: string;
  webp600: string;
  webp200: string;
}

export interface UploadProgress {
  status: string;
  progress: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageUploadService {
  constructor(private storage: Storage, private mediaService: MediaService) {}

  /**
   * Upload an image with 3 WebP variants + original
   * Uses same storage pattern as media gallery: media/{timestamp}-{baseName}-{variant}.{ext}
   */
  async uploadImageWithVariants(
    file: File, 
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ImageVariants> {
    const timestamp = Date.now();
    const baseFileName = this.sanitizeFileName(file.name.replace(/\.[^/.]+$/, ''));
    const extension = this.getFileExtension(file.name);

    try {
      // Upload original (25% progress)
      onProgress?.({ status: 'Origineel uploaden...', progress: 10 });
      const originalFileName = `media/${timestamp}-${baseFileName}-original${extension}`;
      const originalRef = ref(this.storage, originalFileName);
      await uploadBytes(originalRef, file);
      const originalUrl = await getDownloadURL(originalRef);
      onProgress?.({ status: 'Origineel geüpload', progress: 25 });

      // Create and upload WebP 1400px variant
      onProgress?.({ status: 'WebP 1400px variant genereren...', progress: 30 });
      const webp1400Url = await this.createAndUploadWebPVariant(file, 1400, timestamp, baseFileName, 'webp1400');
      onProgress?.({ status: 'WebP 1400px variant geüpload', progress: 50 });

      // Create and upload WebP 600px variant
      onProgress?.({ status: 'WebP 600px variant genereren...', progress: 55 });
      const webp600Url = await this.createAndUploadWebPVariant(file, 600, timestamp, baseFileName, 'webp600');
      onProgress?.({ status: 'WebP 600px variant geüpload', progress: 75 });

      // Create and upload WebP 200px variant
      onProgress?.({ status: 'WebP 200px variant genereren...', progress: 80 });
      const webp200Url = await this.createAndUploadWebPVariant(file, 200, timestamp, baseFileName, 'webp200');
      onProgress?.({ status: 'Registreren...', progress: 95 });

      // Register in Firestore for fast querying and stats
      await this.mediaService.addMediaDocument({
        name: `${timestamp}-${baseFileName}-original${extension}`,
        originalUrl,
        webp1400Url,
        webp600Url,
        webp200Url,
        storagePath: originalFileName,
        size: file.size,
        contentType: file.type
      });

      onProgress?.({ status: 'Alle varianten geüpload', progress: 100 });

      return {
        original: originalUrl,
        webp1400: webp1400Url,
        webp600: webp600Url,
        webp200: webp200Url
      };
    } catch (error) {
      console.error('Error uploading image variants:', error);
      throw error;
    }
  }

  private async createAndUploadWebPVariant(
    file: File, 
    maxWidth: number, 
    timestamp: number,
    baseFileName: string,
    variantName: string
  ): Promise<string> {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: maxWidth,
        useWebWorker: true,
        fileType: 'image/webp' as const,
        initialQuality: 0.85
      };

      const compressedFile = await imageCompression(file, options);
      
      // Same pattern as media gallery: media/{timestamp}-{baseName}-{variant}.webp
      const fileName = `media/${timestamp}-${baseFileName}-${variantName}.webp`;
      const storageRef = ref(this.storage, fileName);
      
      await uploadBytes(storageRef, compressedFile);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error(`Error creating ${variantName} variant:`, error);
      throw error;
    }
  }

  /**
   * Compress a single image and upload to a custom storage path.
   * Useful for logos, favicons, etc. that need their own storage location.
   */
  async compressAndUpload(
    file: File,
    storagePath: string,
    maxWidthOrHeight: number = 800
  ): Promise<string> {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight,
        useWebWorker: true,
        fileType: 'image/webp' as const,
        initialQuality: 0.85
      };

      const compressedFile = await imageCompression(file, options);
      const storageRef = ref(this.storage, storagePath);
      await uploadBytes(storageRef, compressedFile);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error compressing and uploading image:', error);
      throw error;
    }
  }

  private getFileExtension(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext ? `.${ext}` : '';
  }

  private sanitizeFileName(name: string): string {
    // Remove special characters and spaces, replace with dashes
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
